import { useEffect } from "react";
import { useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { portalKeys, queryClient } from "./query";
import type { PortalBootstrap, PortalFlowState } from "@/server/services/portal-bootstrap";

export type { PortalBootstrap, PortalFlowState };

const STALE = 2 * 60_000;
const GC = 30 * 60_000;

const getJson = (url: string) => async () => {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`fetch_failed:${url}`);
  return res.json();
};

/**
 * One canonical query definition per portal GET. Sections and shells read through these
 * (via the shared cache), and the preflight warms them all on entry, so clicking a tab
 * renders from cache instead of a fresh fetch + skeleton (D44).
 */
export const portalQueries = {
  bootstrap: (id: number) => queryOptions({ queryKey: portalKeys.bootstrap(id), queryFn: getJson(`/api/school/${id}/bootstrap`), staleTime: STALE, gcTime: GC }),
  overview: (id: number) => queryOptions({ queryKey: portalKeys.overview(id), queryFn: getJson(`/api/school/${id}/overview`), staleTime: STALE, gcTime: GC }),
  progress: (id: number) => queryOptions({ queryKey: portalKeys.progress(id), queryFn: getJson(`/api/school/${id}/progress`), staleTime: STALE, gcTime: GC }),
  mySchools: () => queryOptions({ queryKey: portalKeys.mySchools(), queryFn: getJson(`/api/school/my-schools`), staleTime: STALE, gcTime: GC }),
  contacts: (id: number) => queryOptions({ queryKey: portalKeys.contacts(id), queryFn: getJson(`/api/school/${id}/contacts`), staleTime: STALE, gcTime: GC }),
  classes: (id: number) => queryOptions({ queryKey: portalKeys.classes(id), queryFn: getJson(`/api/school/${id}/classes`), staleTime: STALE, gcTime: GC }),
  constraints: (id: number) => queryOptions({ queryKey: portalKeys.constraints(id), queryFn: getJson(`/api/school/${id}/constraints`), staleTime: STALE, gcTime: GC }),
  selections: (id: number) => queryOptions({ queryKey: portalKeys.selections(id), queryFn: getJson(`/api/school/${id}/selections`), staleTime: STALE, gcTime: GC }),
  eligible: (id: number) => queryOptions({ queryKey: portalKeys.eligible(id), queryFn: getJson(`/api/school/${id}/eligible`), staleTime: STALE, gcTime: GC }),
};

/**
 * Prefetch every (tiny) portal GET once on entry, so every tab is warm before it is
 * clicked. Represented as a sentinel query so React Strict Mode / multiple callers do
 * not duplicate the work. Data volume is tiny (one school), so prefetching all is cheap.
 */
export function usePortalPreflight(schoolId: number) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!Number.isFinite(schoolId)) return;
    void qc.ensureQueryData({
      queryKey: portalKeys.preflight(schoolId),
      staleTime: Infinity,
      gcTime: GC,
      queryFn: async () => {
        await Promise.allSettled([
          qc.prefetchQuery(portalQueries.bootstrap(schoolId)),
          qc.prefetchQuery(portalQueries.overview(schoolId)),
          qc.prefetchQuery(portalQueries.progress(schoolId)),
          qc.prefetchQuery(portalQueries.mySchools()),
          qc.prefetchQuery(portalQueries.contacts(schoolId)),
          qc.prefetchQuery(portalQueries.classes(schoolId)),
          qc.prefetchQuery(portalQueries.constraints(schoolId)),
          qc.prefetchQuery(portalQueries.selections(schoolId)),
          qc.prefetchQuery(portalQueries.eligible(schoolId)),
        ]);
        return true;
      },
    });
  }, [qc, schoolId]);
}

/** Read prefetched data synchronously (for cache-seeding a section's initial state). */
export function readPortalCache<T = unknown>(key: readonly unknown[]): T | undefined {
  return queryClient.getQueryData<T>(key);
}

/** Write a section's freshly fetched data back into the cache, so the next time the tab
 *  mounts it seeds instantly from up-to-date data (not the older preflight snapshot). */
export function writePortalCache<T = unknown>(key: readonly unknown[], data: T): void {
  queryClient.setQueryData<T>(key, data);
}

/** Fetch through the shared cache: instant if warm, otherwise fetches once.
 *  `force` bypasses staleness after a mutation. */
export function loadPortal<T = unknown>(opts: { queryKey: readonly unknown[]; queryFn: any; staleTime?: number; gcTime?: number }, force = false): Promise<T> {
  return force
    ? queryClient.fetchQuery({ ...opts, staleTime: 0 })
    : queryClient.ensureQueryData(opts);
}

/**
 * The shared portal bootstrap: critical branch facts (flow state, deadline, next step)
 * fetched once and cached, so every page renders the right UI on first paint and tab
 * switches are instant. Reuses the TanStack cache, so the first caller pays for it and
 * the rest read it for free.
 */
export function usePortalBootstrap(schoolId: number) {
  return useQuery({
    queryKey: portalKeys.bootstrap(schoolId),
    queryFn: async (): Promise<PortalBootstrap> => {
      const res = await fetch(`/api/school/${schoolId}/bootstrap`, { credentials: "include" });
      if (!res.ok) throw new Error("bootstrap_failed");
      return res.json();
    },
    enabled: Number.isFinite(schoolId),
  });
}

/** Warm the overview cache so returning to the Overzicht tab is instant. Safe to call
 *  repeatedly (TanStack dedupes and respects staleTime). */
export function prefetchOverview(schoolId: number) {
  if (!Number.isFinite(schoolId)) return;
  void queryClient.prefetchQuery(portalQueries.overview(schoolId));
}

export type PortalTab = "overzicht" | "schoolgegevens" | "programmaselectie";

/** Intent prefetch: on hover/focus/pointerdown of a tab, warm exactly that tab's data.
 *  prefetchQuery respects staleTime and dedupes, so rapid hovers are cheap and a fresh
 *  cache is a no-op. The visit itself still revalidates (the sections' load()). */
export function prefetchTab(schoolId: number, tab: PortalTab) {
  if (!Number.isFinite(schoolId)) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const warm = (opts: any) => void queryClient.prefetchQuery(opts);
  switch (tab) {
    case "overzicht":
      warm(portalQueries.overview(schoolId));
      warm(portalQueries.bootstrap(schoolId));
      break;
    case "schoolgegevens":
      warm(portalQueries.progress(schoolId));
      warm(portalQueries.contacts(schoolId));
      warm(portalQueries.classes(schoolId));
      warm(portalQueries.constraints(schoolId));
      break;
    case "programmaselectie":
      warm(portalQueries.progress(schoolId));
      warm(portalQueries.selections(schoolId));
      warm(portalQueries.eligible(schoolId));
      break;
  }
}

/** Format an ISO timestamp as a Dutch long date ("22 juni 2026"). Used wherever the
 *  app-wide planning deadline is shown to schools. Returns "" for a null/empty value. */
export function formatDutchDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
}

/** Convenience: collapse a flow state into the booleans the review pages use. */
export function flowFlags(state: PortalFlowState | undefined) {
  if (!state || state.kind === "never_submitted") {
    return {
      everSubmitted: false,
      hasUnsubmittedChanges: true,
      lastSubmittedAt: null as string | null,
      lastEditedAt: (state?.lastEditedAt ?? null) as string | null,
      dependencyChanged: false,
    };
  }
  return {
    everSubmitted: true,
    hasUnsubmittedChanges: state.kind === "submitted_with_pending_changes",
    lastSubmittedAt: state.submittedAt,
    lastEditedAt: state.lastEditedAt,
    dependencyChanged: state.kind === "submitted_with_pending_changes" ? state.dependencyChanged : false,
  };
}
